import { Component, ElementRef, NgZone } from '@angular/core';
import { RGB } from '../hero-motif/hero-motif.base';
import { Branch, HeroSakuraComponent, Petal, Spray } from '../hero-sakura/hero-sakura.component';

// The Canadian maple leaf (the flag's), its right half from the top point down to the stem, in leaf
// units: tips and the notches between them; the left half is its mirror. The central lobe has its top
// point and two side points, each side lobe three points, each bottom lobe one.
const MAPLE_HALF: [ number, number ][] = [
  [ 0, -1 ], [ 0.1, -0.74 ], [ 0.24, -0.82 ], [ 0.17, -0.42 ],
  [ 0.4, -0.6 ], [ 0.46, -0.5 ], [ 0.8, -0.64 ], [ 0.66, -0.38 ],
  [ 0.84, -0.27 ], [ 0.46, -0.05 ], [ 0.58, 0.14 ], [ 0.08, 0.1 ],
];

// A fallen leaf lying on the ground.
interface Litter {
  x: number;
  y: number;
  size: number;
  tint: number;
  turn: number;
  /** Its width against its length: a leaf seen lying down is foreshortened. */
  lie: number;
  age: number;
}

// how many fallen leaves the drift keeps; past it the oldest gives way to the newest
const LITTER = 2100;

/**
 * Autumn's hero board: a sugar maple's branch in the fall (owner, 2026-10-16: Sakura's idea, but a
 * Canadian maple, its leaves flying). The branch reaches up from beyond the lower left, its grey-brown
 * limbs spreading and drooping a little, its maple leaves (the flag's leaf) hanging in opposite pairs
 * on their stalks and in clusters at the tips: rust, burnt orange and gold (owner: no greens). They
 * let go and drift across the page, tumbling as they go, and gather in a mound under the tree at the
 * bottom left. The ground is a soft mesh of red, orange and yellow patches on white. A gust (the pointer, a finger,
 * the counter counting) shakes the branch and sends the leaves flying.
 */
@Component( {
  selector: 'app-hero-autumn',
  template: '<canvas #canvas class="hs-canvas" aria-hidden="true"></canvas>',
  styles: [ ':host { display: block; position: absolute; inset: 0; overflow: hidden; pointer-events: none; }',
    '.hs-canvas { display: block; position: absolute; inset: 0; }' ],
} )
export class HeroAutumnComponent extends HeroSakuraComponent {

  private litter: Litter[] = [];
  // the settled leaves are painted once into their own canvas and redrawn only when the mound changes
  private ground: HTMLCanvasElement | null = null;
  private groundDirty = true;
  private lift = 0;

  constructor( zone: NgZone, host: ElementRef<HTMLElement> ) {
    super( zone, host );
    this.petalSize = [ 22, 14 ];
    this.turnFlowers = true;
    this.meshStrength = 1.4;
  }

  /** How far the mound reaches from the left edge. */
  private moundWidth(): number {
    return Math.min( this.width * 0.3, 460 * this.scaleK );
  }

  /**
   * The mound of fallen leaves under the tree, only at the left (owner, 2026-10-16: a hill, no gaps):
   * its height at x, highest at the edge and falling away to nothing at its foot.
   */
  private drift( x: number ): number {
    const u = x / this.moundWidth();
    if ( u >= 1 ) { return 0; }
    return 78 * this.scaleK * Math.pow( 1 - u * u, 0.75 );
  }

  /**
   * The pointer moving over the mound stirs it (owner, 2026-10-16): the leaves under it are thrown up
   * and fly off on the wind like the ones from the branch, and come down again onto the mound.
   */
  private stir( dt: number ): void {
    if ( this.near < 0.2 || this.pointerX < -1e3 ) { return; }
    const top = this.drift( Math.max( 0, this.pointerX ) );
    if ( this.pointerX > this.moundWidth() + 30 || this.pointerY < this.height - top - 50 ) { return; }
    // a gentle stir, a few leaves at a time (owner: not a fountain)
    this.lift += dt * 14 * this.near;
    while ( this.lift >= 1 ) {
      this.lift -= 1;
      // a leaf near the pointer, the nearest of a few picked at random
      let best = -1;
      let bestD = 90 * 90;
      for ( let n = 0; n < 24; n++ ) {
        const i = Math.floor( Math.random() * this.litter.length );
        const l = this.litter[ i ];
        const d = ( l.x - this.pointerX ) ** 2 + ( l.y - this.pointerY ) ** 2;
        if ( d < bestD ) { bestD = d; best = i; }
      }
      if ( best < 0 ) { continue; }
      // the mound stays whole: the leaf thrown up is one off its top, and the ones that come down again
      // join it, the oldest giving way
      const l = this.litter[ best ];
      const p = this.petals.find( q => q.age > 2 && !q.ky ) || this.petals[ Math.floor( Math.random() * this.petals.length ) ];
      if ( !p ) { return; }
      Object.assign( p, this.petal( l.x, l.y - 4 ) );
      p.tint = l.tint;
      p.age = 10;
      p.kx = ( l.x - this.pointerX ) * 0.8 + ( Math.random() - 0.3 ) * 50;
      p.ky = -( 65 + Math.random() * 85 );
    }
  }

  /** A leaf come down onto the mound lies there; the mound keeps its newest leaves. */
  protected land( p: Petal ): boolean {
    const top = this.drift( p.x );
    // a leaf just thrown up off the mound flies before it can settle again
    if ( top <= 0 || p.x < 0 || ( p.ky || 0 ) < -20 ) { return false; }
    const ground = this.height - top * ( 0.75 + Math.random() * 0.25 );
    if ( p.y < ground ) { return false; }
    this.lay( p.x, p.y, p.size, p.tint, 0 );
    if ( this.litter.length > LITTER ) { this.litter.splice( 0, this.litter.length - LITTER ); }
    this.groundDirty = true;
    return true;
  }

  private lay( x: number, y: number, size: number, tint: number, age: number ): void {
    this.litter.push( { x, y: Math.min( y, this.height + 4 ), size, tint, turn: Math.random() * Math.PI * 2, lie: 0.7 + Math.random() * 0.3, age } );
    this.groundDirty = true;
  }

  protected build( rand: () => number ): void {
    super.build( rand );
    // the mound is already there when the page opens, its leaves packed so no gap shows
    this.litter = [];
    const k = this.scaleK;
    for ( let i = 0; i < LITTER * 0.92; i++ ) {
      const x = rand() * this.moundWidth() * 1.02;
      // spread evenly through the mound's depth, not only over its top, so its foot shows no gaps
      const y = this.height - this.drift( x ) * rand();
      this.litter.push( {
        x, y: y + 6, size: ( 20 + rand() * 14 ) * k, tint: Math.floor( rand() * 3 ),
        turn: rand() * Math.PI * 2, lie: 0.7 + rand() * 0.3, age: 10,
      } );
    }
    // and a packed bottom row along the stage's edge, under the whole mound (owner: no white showing)
    for ( let x = 0; x < this.moundWidth(); x += 9 * k ) {
      for ( let r = 0; r < 3; r++ ) {
        this.litter.push( {
          x: x + rand() * 9 * k, y: this.height - rand() * 14 * k + 6, size: ( 22 + rand() * 12 ) * k,
          tint: Math.floor( rand() * 3 ), turn: rand() * Math.PI * 2, lie: 0.7 + rand() * 0.3, age: 10,
        } );
      }
    }
    // the lowest leaves are the farthest under: draw from the top of the drift down
    this.litter.sort( ( a, b ) => a.y - b.y );
    this.groundDirty = true;
  }

  /**
   * The mound: leaves only (owner: no soil under them), packed so nothing shows through, each lying
   * flat (foreshortened), the newest ones fading in.
   */
  protected paintGround( ctx: CanvasRenderingContext2D, dt: number ): void {
    if ( dt > 0 ) { this.stir( dt ); }
    const scale = ctx.getTransform().a;
    // the settled leaves, from their canvas (painted again only when one lands or settles)
    let fresh = false;
    for ( const l of this.litter ) {
      if ( l.age < 1 ) {
        l.age += dt || 1 / 60;
        if ( l.age >= 1 ) { this.groundDirty = true; } else { fresh = true; }
      }
    }
    if ( this.groundDirty || !this.ground ) { this.bakeGround( scale ); }
    ctx.drawImage( this.ground!, 0, this.height - this.ground!.height / scale, this.ground!.width / scale, this.ground!.height / scale );
    // the newest leaves, fading in on top
    if ( fresh ) {
      for ( const l of this.litter ) {
        if ( l.age < 1 ) { this.layLeaf( ctx, l, scale, 0, Math.min( 1, 0.4 + l.age ) ); }
      }
    }
    ctx.globalAlpha = 1;
  }

  private bakeGround( scale: number ): void {
    this.groundDirty = false;
    const w = Math.ceil( ( this.moundWidth() + 80 * this.scaleK ) * scale );
    const h = Math.ceil( ( this.drift( 0 ) + 60 * this.scaleK ) * scale );
    if ( !this.ground || this.ground.width !== w || this.ground.height !== h ) {
      this.ground = document.createElement( 'canvas' );
      this.ground.width = w;
      this.ground.height = h;
    }
    const g = this.ground.getContext( '2d' )!;
    g.setTransform( 1, 0, 0, 1, 0, 0 );
    g.clearRect( 0, 0, w, h );
    // the canvas' top is this far below the board's top, in page pixels
    const offset = this.height - h / scale;
    for ( const l of this.litter ) {
      if ( l.age >= 1 ) { this.layLeaf( g, l, scale, offset, 1 ); }
    }
  }

  /** One fallen leaf lying flat: squashed across the ground's line, then turned in its own plane. */
  private layLeaf( ctx: CanvasRenderingContext2D, l: Litter, scale: number, offset: number, alpha: number ): void {
    ctx.globalAlpha = alpha;
    const img = this.petalSprites[ l.tint % this.petalSprites.length ];
    const c = Math.cos( l.turn );
    const s = Math.sin( l.turn );
    ctx.setTransform( scale, 0, 0, scale * l.lie, l.x * scale, ( l.y - offset ) * scale );
    ctx.transform( c, s, -s, c, 0, 0 );
    ctx.drawImage( img, -l.size / 2, -l.size / 2, l.size, l.size );
    ctx.setTransform( scale, 0, 0, scale, 0, 0 );
  }

  protected colorsChanged(): void {
    super.colorsChanged();
    this.groundDirty = true;
  }

  /** Only turned leaves fall: rust, burnt orange and gold, about evenly (owner: the green stay on the tree). */
  protected petalTint( r: number ): number {
    return r < 0.36 ? 0 : r < 0.68 ? 1 : 2;
  }

  /** The mesh: white weighing most (owner: red, orange, yellow and white, not all orange). */
  protected meshTints(): [ RGB, RGB ] {
    const [ cream ] = this.five();
    return [ cream, this.mix( cream, [ 255, 255, 255 ], 0.75 ) ];
  }

  /** Its three patches: the rust, the burnt orange and the gold, each softened toward white. */
  protected meshPatches(): RGB[] {
    const [ , rust, burnt, gold ] = this.five();
    const white: RGB = [ 255, 255, 255 ];
    return [ this.mix( rust, white, 0.62 ), this.mix( burnt, white, 0.55 ), this.mix( gold, white, 0.5 ) ];
  }

  /** A maple's bark: grey-brown, the circle's burnt orange deep in shadow. */
  protected barkColor(): RGB {
    const [ , , burnt ] = this.five();
    return this.mix( burnt, [ 58, 48, 44 ], 0.78 );
  }

  /** The colour circle's five, or autumn's own when the theme gives none: cream, rust, burnt orange, gold, green. */
  protected five(): RGB[] {
    if ( this.paletteColors.length >= 5 ) { return this.paletteColors.slice( 0, 5 ); }
    return [ [ 244, 211, 189 ], [ 190, 85, 62 ], [ 176, 99, 58 ], [ 222, 165, 29 ], [ 14, 129, 19 ] ];
  }

  /**
   * The maple's branch: one strong limb from beyond the lower left, angled up into the stage (owner),
   * forking wide (one branch splitting into arms, not two apart), fitted into the left of the stage so
   * the words stay clear.
   */
  protected branches( rand: () => number, tall: boolean ): Branch[] {
    const k = this.scaleK;
    const W = this.width;
    const H = this.height;
    if ( tall ) {
      return [ { x: -W * 0.06, y: H * 0.32, angle: -0.5, len: 200 * k, width: 20 * k, depth: 4, droop: 0.05, spread: 1.7, radius: Math.min( W * 0.62, H * 0.3 ), top: 50 * k } ];
    }
    // one big limb rising from the lower left (owner: angled up); its first fork sends one arm up the
    // left side and one on toward the middle
    // placed and sized by the stage's height, so a wider screen shows the same tree, not a bigger one
    return [ { x: -H * 0.08, y: H * 0.62, angle: -0.62, len: 300 * k, width: 28 * k, depth: 5, droop: 0.03, spread: 1.9,
      radius: Math.min( W * 0.36, H * 0.9 ), top: 50 * k } ];
  }

  /**
   * The leaves: along each limb, opposite pairs on their stalks (a maple's leaves face each other on
   * the twig), and at each tip a small cluster spread like a hand. A leaf is stored as its stalk (dx,
   * dy: from the twig to the leaf's base) and the way it points (turn); all turned, no greens (owner).
   */
  protected dress( b: Branch, rand: () => number ): void {
    const k = this.scaleK;
    const tint = () => Math.floor( rand() * 3 );
    const leaf = ( angle: number, size: number ): Spray[ 'flowers' ][ number ] => {
      // a leaf hangs a little toward the ground on its stalk, and the stalk is never a straight stick:
      // it curves one way or the other (owner, 2026-10-16: "eğri büğrü")
      const hang = angle + 0.3 * Math.sin( Math.PI - angle );
      const stalk = size * ( 0.3 + rand() * 0.2 );
      return { dx: Math.sin( hang ) * stalk, dy: -Math.cos( hang ) * stalk, size, tint: tint(), turn: hang, bend: ( rand() - 0.5 ) * 0.9 };
    };
    for ( let i = b.first!; i < b.last!; i++ ) {
      const l = this.limbs[ i ];
      if ( l.depth < 1 ) { continue; }
      // which way the limb runs, roughly (the sway moves it only a little): its angle from the root
      let dir = 0;
      for ( let j = i; j >= 0; j = this.limbs[ j ].parent ) { dir += this.limbs[ j ].angle; if ( this.limbs[ j ].parent < 0 ) { break; } }
      // a leaf's "up" is at turn 0; along the limb is dir + 90 degrees
      const along = dir + Math.PI / 2;
      // fewer leaves than a full crown, so the branch shows (owner, 2026-10-16)
      const pairs = l.twig ? 0 : rand() < 0.6 ? 1 : 0;
      for ( let p = 0; p < pairs; p++ ) {
        const flowers = [ -1, 1 ].map( side => leaf( along + side * ( 1.1 + rand() * 0.35 ) + 0.25, ( 46 + rand() * 22 ) * k ) );
        this.sprays.push( { limb: i, at: 0.3 + 0.45 * ( p + rand() * 0.5 ) / Math.max( 1, pairs ), flowers, leaves: [] } );
      }
      if ( l.depth >= b.depth - 1 || l.twig ) {
        const n = l.twig ? 1 + Math.floor( rand() * 2 ) : 2 + Math.floor( rand() * 3 );
        const flowers: Spray[ 'flowers' ] = [];
        for ( let f = 0; f < n; f++ ) {
          const spread = ( f - ( n - 1 ) / 2 ) * 0.55 + ( rand() - 0.5 ) * 0.25;
          flowers.push( leaf( along + spread + 0.15, ( l.twig ? 38 : 50 ) * ( 0.85 + rand() * 0.35 ) * k ) );
        }
        this.sprays.push( { limb: i, at: 1, flowers, leaves: [] } );
      }
    }
  }

  /** A leaf on its stalk: the stalk from the twig to the leaf's base, the leaf pointing out along it. */
  protected drawFlower( ctx: CanvasRenderingContext2D, x: number, y: number, f: Spray[ 'flowers' ][ number ] ): void {
    const img = this.flowerSprites[ f.tint % this.flowerSprites.length ];
    // a leaf flutters on its stalk, a little more in a gust
    const flutter = Math.sin( this.time * 0.9 + f.size ) * 0.06 * ( 1 + 2 * this.energy );
    const bx = x + f.dx;
    const by = y + f.dy;
    // the stalk: a curve, bowed sideways by its bend and sagging a little, and the leaf carries on
    // from where the curve ends
    const len = Math.hypot( f.dx, f.dy ) || 1;
    const nx = -f.dy / len;
    const ny = f.dx / len;
    const bend = f.bend || 0;
    const cx = x + f.dx / 2 + nx * bend * len;
    const cy = y + f.dy / 2 + ny * bend * len + len * 0.18;
    ctx.strokeStyle = this.rgba( this.barkColor(), 1 );
    ctx.lineWidth = Math.max( 1, f.size * 0.035 );
    ctx.beginPath();
    ctx.moveTo( x, y );
    ctx.quadraticCurveTo( cx, cy, bx, by );
    ctx.stroke();
    const turn = Math.atan2( bx - cx, -( by - cy ) ) + flutter;
    // the sprite's stem end sits at 0.2 of its height below its middle, so the leaf grows from the stalk
    const ux = Math.sin( turn );
    const uy = -Math.cos( turn );
    this.sprite( ctx, img, bx + ux * f.size * 0.2, by + uy * f.size * 0.2, f.size, f.size, turn );
  }

  /**
   * The leaves, painted once per colour change: the flag's maple leaf, lighter toward its middle, its
   * veins running out to the lobes' main points, in rust, burnt orange and gold.
   */
  protected makeSprites(): void {
    const [ , rust, burnt, gold ] = this.five();
    this.flowerSprites = [ rust, burnt, gold ].map( c => this.maple( c ) );
    this.petalSprites = [ rust, burnt, gold ].map( c => this.maple( c ) );
    this.leafSprite = null;
  }

  private maple( tint: RGB ): HTMLCanvasElement {
    const size = 128;
    const unit = size * 0.42;
    const c = document.createElement( 'canvas' );
    c.width = c.height = size;
    const g = c.getContext( '2d' )!;
    g.translate( size / 2, size / 2 );
    const outline = [ ...MAPLE_HALF, ...MAPLE_HALF.slice().reverse().map( ( [ x, y ] ) => [ -x, y ] as [ number, number ] ) ];
    g.beginPath();
    outline.forEach( ( [ x, y ], i ) => i ? g.lineTo( x * unit, y * unit ) : g.moveTo( x * unit, y * unit ) );
    g.closePath();
    const fill = g.createRadialGradient( 0, -unit * 0.2, 0, 0, -unit * 0.1, unit * 1.05 );
    fill.addColorStop( 0, this.rgba( this.mix( tint, [ 255, 238, 196 ], 0.25 ), 1 ) );
    fill.addColorStop( 0.6, this.rgba( tint, 1 ) );
    fill.addColorStop( 1, this.rgba( this.mix( tint, [ 50, 22, 12 ], 0.25 ), 1 ) );
    g.fillStyle = fill;
    g.fill();
    // the veins out to the main points, and the stem
    const vein = this.mix( tint, [ 60, 24, 12 ], 0.42 );
    g.strokeStyle = this.rgba( vein, 0.5 );
    g.lineWidth = 1.6;
    g.lineCap = 'round';
    for ( const [ x, y ] of [ [ 0, -1 ], [ 0.8, -0.64 ], [ -0.8, -0.64 ], [ 0.58, 0.14 ], [ -0.58, 0.14 ] ] ) {
      g.beginPath();
      g.moveTo( 0, unit * 0.1 );
      g.lineTo( x * unit * 0.88, y * unit * 0.88 );
      g.stroke();
    }
    // the stem: short, and bending as it leaves the leaf, never a straight stick (owner, 2026-10-16)
    g.strokeStyle = this.rgba( vein, 1 );
    g.lineWidth = 2.6;
    g.beginPath();
    g.moveTo( 0, unit * 0.1 );
    g.quadraticCurveTo( unit * 0.13, unit * 0.3, unit * 0.06, unit * 0.48 );
    g.stroke();
    return c;
  }
}
